import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import React, { useMemo } from 'react'

interface LogDetailViewProps {
	data: Record<string, unknown>
}

interface JsonRendered {
	isJson: boolean
	html: string
}

const escapeHtml = (str: string): string =>
	str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;')

const tokenRegex =
	/"(?:\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(?:\s*:)?|\b(?:true|false|null)\b|-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?/g

const colorForToken = (token: string): string => {
	if (token.startsWith('"')) {
		return /:\s*$/.test(token) ? '#a31515' : '#0b7500'
	}
	if (token === 'true' || token === 'false') {
		return '#0000ff'
	}
	if (token === 'null') {
		return '#808080'
	}
	return '#098658'
}

const highlightJson = (prettyJson: string): string => {
	let result = ''
	let lastIndex = 0
	const regex = new RegExp(tokenRegex.source, tokenRegex.flags)
	let match: RegExpExecArray | null
	while ((match = regex.exec(prettyJson)) !== null) {
		result += escapeHtml(prettyJson.slice(lastIndex, match.index))
		const token = match[0]
		result += `<span style="color:${colorForToken(token)}">${escapeHtml(token)}</span>`
		lastIndex = regex.lastIndex
	}
	result += escapeHtml(prettyJson.slice(lastIndex))
	return result
}

const tryRenderJson = (value: unknown): JsonRendered => {
	let parsed: unknown = value

	if (typeof value === 'string') {
		const trimmed = value.trim()
		if (trimmed.length >= 2 && (trimmed.startsWith('{') || trimmed.startsWith('['))) {
			try {
				parsed = JSON.parse(trimmed)
			} catch {
				return { isJson: false, html: '' }
			}
		} else {
			return { isJson: false, html: '' }
		}
	} else if (value === null || typeof value !== 'object') {
		return { isJson: false, html: '' }
	}

	try {
		const pretty = JSON.stringify(parsed, null, 2)
		if (pretty === undefined) {
			return { isJson: false, html: '' }
		}
		return { isJson: true, html: highlightJson(pretty) }
	} catch {
		return { isJson: false, html: '' }
	}
}

const formatPrimitive = (value: unknown): string => {
	if (value === null) {
		return 'null'
	}
	if (value === undefined) {
		return ''
	}
	if (typeof value === 'string') {
		return value
	}
	if (typeof value === 'number' || typeof value === 'boolean') {
		return String(value)
	}
	return JSON.stringify(value)
}

const FieldValue: React.FC<{ value: unknown }> = ({ value }) => {
	const jsonRender = useMemo(() => tryRenderJson(value), [value])

	if (jsonRender.isJson) {
		return (
			<Box
				component="pre"
				sx={{
					margin: 0,
					padding: 1.25,
					backgroundColor: '#f7f7f7',
					border: '1px solid #e0e0e0',
					borderRadius: 1,
					fontFamily: 'monospace',
					fontSize: '0.8em',
					whiteSpace: 'pre-wrap',
					wordBreak: 'break-word',
					maxHeight: 420,
					overflow: 'auto',
				}}
				dangerouslySetInnerHTML={{ __html: jsonRender.html }}
			/>
		)
	}

	const text = formatPrimitive(value)
	return (
		<Box
			sx={{
				fontFamily: 'monospace',
				fontSize: '0.85em',
				whiteSpace: 'pre-wrap',
				wordBreak: 'break-word',
			}}
		>
			{text}
		</Box>
	)
}

export const LogDetailView: React.FC<LogDetailViewProps> = ({ data }) => {
	const entries = useMemo(() => {
		const keys = Object.keys(data).sort((a, b) => a.localeCompare(b))
		return keys.map((key) => [key, data[key]] as const)
	}, [data])

	if (entries.length === 0) {
		return <Box>No fields to display.</Box>
	}

	return (
		<Table size="small">
			<TableBody>
				{entries.map(([key, value]) => (
					<TableRow key={key} sx={{ verticalAlign: 'top' }}>
						<TableCell sx={{ fontWeight: 'bold', width: 220, whiteSpace: 'nowrap' }}>{key}</TableCell>
						<TableCell>
							<FieldValue value={value} />
						</TableCell>
					</TableRow>
				))}
			</TableBody>
		</Table>
	)
}
